import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { formatDate } from '../lib/utils';
import { PageHeader } from '../components/shared/PageHeader';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { StatusBadge } from '../components/shared/StatusBadge';
import { UrgencyBadge } from '../components/shared/UrgencyBadge';
import { useAppContext } from '../lib/store';
import { Edit, Mail, Phone, MapPin, Building, ArrowRight } from 'lucide-react';
export function CustomerDetailPage() {
  const {
    id
  } = useParams();
  const navigate = useNavigate();
  const {
    customers,
    workOrders
  } = useAppContext();
  const customer = customers.find(c => c.id === id);
  if (!customer) {
    return <div data-id="element-185">Stranka ne obstaja.</div>;
  }
  const customerWorkOrders = workOrders.filter(wo => wo.customer_id === id).sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
  return <div className="space-y-6" data-id="element-186">
      <PageHeader title={customer.name} description="Podrobnosti stranke in pregled delovnih nalogov." data-id="element-187">
        <Button onClick={() => navigate(`/stranke/${id}/uredi`)} data-id="element-188">
          <Edit className="w-4 h-4 mr-2" data-id="element-189" />
          Uredi
        </Button>
      </PageHeader>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-190">
        {/* Customer Info */}
        <Card className="md:col-span-1" data-id="element-191">
          <CardHeader data-id="element-192">
            <CardTitle className="text-lg flex items-center justify-between" data-id="element-193">
              Osnovni podatki
              {customer.is_active ? <Badge variant="default" className="bg-green-100 text-green-700" data-id="element-194">
                  Aktivna
                </Badge> : <Badge variant="secondary" data-id="element-195">Neaktivna</Badge>}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6" data-id="element-196">
            <div data-id="element-197">
              <p className="text-sm text-slate-500 mb-1" data-id="element-198">Predpona nalogov</p>
              <Badge variant="outline" className="font-mono text-base" data-id="element-199">
                {customer.prefix}
              </Badge>
            </div>

            <div className="space-y-3" data-id="element-200">
              <div className="flex items-start gap-3" data-id="element-201">
                <Building className="w-5 h-5 text-slate-400 mt-0.5" data-id="element-202" />
                <div data-id="element-203">
                  <p className="text-sm font-medium" data-id="element-204">Davčna številka</p>
                  <p className="text-sm text-slate-600" data-id="element-205">
                    {customer.tax_number || '/'}
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3" data-id="element-206">
                <MapPin className="w-5 h-5 text-slate-400 mt-0.5" data-id="element-207" />
                <div data-id="element-208">
                  <p className="text-sm font-medium" data-id="element-209">Naslov</p>
                  <p className="text-sm text-slate-600" data-id="element-210">
                    {customer.address}
                    <br data-id="element-211" />
                    {customer.postal_code} {customer.city}
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3" data-id="element-212">
                <Mail className="w-5 h-5 text-slate-400 mt-0.5" data-id="element-213" />
                <div data-id="element-214">
                  <p className="text-sm font-medium" data-id="element-215">E-pošta</p>
                  <p className="text-sm text-slate-600" data-id="element-216">
                    {customer.email || '/'}
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3" data-id="element-217">
                <Phone className="w-5 h-5 text-slate-400 mt-0.5" data-id="element-218" />
                <div data-id="element-219">
                  <p className="text-sm font-medium" data-id="element-220">Telefon</p>
                  <p className="text-sm text-slate-600" data-id="element-221">
                    {customer.phone || '/'}
                  </p>
                </div>
              </div>
            </div>

            {customer.notes && <div className="pt-4 border-t" data-id="element-222">
                <p className="text-sm font-medium mb-1" data-id="element-223">Opombe</p>
                <p className="text-sm text-slate-600 whitespace-pre-wrap" data-id="element-224">
                  {customer.notes}
                </p>
              </div>}
          </CardContent>
        </Card>

        {/* Work Orders List */}
        <Card className="md:col-span-2" data-id="element-225">
          <CardHeader className="flex flex-row items-center justify-between" data-id="element-226">
            <CardTitle className="text-lg" data-id="element-227">
              Delovni nalogi ({customerWorkOrders.length})
            </CardTitle>
            <Button variant="outline" size="sm" onClick={() => navigate('/nalogi/nov')} data-id="element-228">
              Nov nalog za stranko
            </Button>
          </CardHeader>
          <CardContent data-id="element-229">
            {customerWorkOrders.length > 0 ? <div className="rounded-md border" data-id="element-230">
                <Table data-id="element-231">
                  <TableHeader data-id="element-232">
                    <TableRow data-id="element-233">
                      <TableHead data-id="element-234">Številka</TableHead>
                      <TableHead data-id="element-235">Datum</TableHead>
                      <TableHead data-id="element-236">Nujnost</TableHead>
                      <TableHead data-id="element-237">Status</TableHead>
                      <TableHead data-id="element-238"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody data-id="element-239">
                    {customerWorkOrders.map(wo => <TableRow key={wo.id} data-id="element-240">
                        <TableCell className="font-medium text-primary" data-id="element-241">
                          {wo.work_order_number}
                        </TableCell>
                        <TableCell data-id="element-242">{formatDate(wo.execution_date)}</TableCell>
                        <TableCell data-id="element-243">
                          <UrgencyBadge urgency={wo.urgency} data-id="element-244" />
                        </TableCell>
                        <TableCell data-id="element-245">
                          <StatusBadge status={wo.status} data-id="element-246" />
                        </TableCell>
                        <TableCell className="text-right" data-id="element-247">
                          <Button variant="ghost" size="sm" onClick={() => navigate(`/nalogi/${wo.id}`)} data-id="element-248">
                            Odpri <ArrowRight className="w-4 h-4 ml-1" data-id="element-249" />
                          </Button>
                        </TableCell>
                      </TableRow>)}
                  </TableBody>
                </Table>
              </div> : <div className="text-center py-12 text-slate-500 border rounded-lg border-dashed" data-id="element-250">
                Za to stranko še ni ustvarjenih delovnih nalogov.
              </div>}
          </CardContent>
        </Card>
      </div>
    </div>;
}